// Dashboard.jsx
import { MdOutlineLocalPhone } from "react-icons/md";
import React, { useState, useEffect } from "react";
import {
  Layout,
  Menu,
  message,
  Avatar,
  Modal,
  Dropdown,
  Space,
  Button,
  notification,
  Grid,
  Drawer,
  Tooltip,
} from "antd";
import {
  AppstoreOutlined,
  ShoppingCartOutlined,
  PlusOutlined,
  LoginOutlined,
  LogoutOutlined,
  FormOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SkinOutlined,
  DownOutlined,
  UserOutlined,
  NotificationOutlined,
  DollarCircleOutlined,
  TagsOutlined,
  ColumnWidthOutlined,
  UnorderedListOutlined,
  FileDoneOutlined,
  BuildOutlined,
  FolderOpenOutlined,
  // NEW: user submenu item icons
  UserSwitchOutlined,
  SafetyCertificateOutlined,
  UserAddOutlined,
  SolutionOutlined,
  QuestionCircleOutlined,
  ExclamationCircleOutlined,
  TruckOutlined,
} from "@ant-design/icons";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import NotificationBell from '../components/NotificationBell'; // Adjust the path as needed
import axiosInstance from "../utils/axiosInstance";
import ChatWidget from "../components/AdminChat";

const { Header, Sider, Content } = Layout;
const { SubMenu } = Menu;
const { useBreakpoint } = Grid;

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [collapsed, setCollapsed] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const [isLoggedIn] = useState(!!localStorage.getItem("isAuthenticated"));

  // Read role + a safe display name from localStorage (no API changes)
  const userRole = localStorage.getItem("userRole") || "";
  const displayName =
    localStorage.getItem("userName") ||
    localStorage.getItem("username") ||
    localStorage.getItem("name") ||
    localStorage.getItem("fullName") ||
    localStorage.getItem("email") ||
    "Admin";
  const roleLabel = (userRole || "guest").toUpperCase();

  const isSuperAdmin = userRole === "super admin";
  const isAdmin = userRole === "admin"; // <-- NEW: regular admin role
  const isSuperAdminOrAdmin = isSuperAdmin || isAdmin; // <-- NEW: combined check

  const isDeliveryUser = userRole === "deliveries";
  const isSalesUser = userRole === "sales";

  // ✅ Added: Production roles
  const isProductionAdmin = userRole === "production admin";
  const isProductionManager = userRole === "production manager";
  const isProdRole = isProductionAdmin || isProductionManager;

  // Redirects for specific roles (landing /home or '/')
  useEffect(() => {
    if (isDeliveryUser && (location.pathname === "/" || location.pathname.startsWith("/home"))) {
      navigate("/order-signed-delivery-note");
    }
    if (isSalesUser && (location.pathname === "/" || location.pathname.startsWith("/home"))) {
      navigate("/all-orders");
    }
    // ✅ Added: both production roles land on production dashboard
    if (isProdRole && (location.pathname === "/" || location.pathname.startsWith("/home"))) {
      navigate("/production-dashboard");
    }
  }, [isDeliveryUser, isSalesUser, isProdRole, location.pathname, navigate]);

  // Prevent sales users from manually landing on production pages:
  useEffect(() => {
    if (
      isSalesUser &&
      (location.pathname.startsWith("/production-dashboard") ||
        location.pathname.startsWith("/production-orders"))
    ) {
      message.error("Access denied: production pages are not available for your role.");
      navigate("/all-orders");
    }
  }, [isSalesUser, location.pathname, navigate]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) navigate("/login");
  }, [navigate]);

  useEffect(() => {
    const interceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("isAuthenticated");
          localStorage.removeItem("token");
          localStorage.removeItem("userRole");
          message.error("Session expired. Please log in again.");
          navigate("/login");
        }
        return Promise.reject(error);
      }
    );
    return () => axiosInstance.interceptors.response.eject(interceptor);
  }, [navigate]);

  // selection/open logic
  let selectedKey = "";
  if (location.pathname.startsWith("/all-orders")) selectedKey = "all-orders";
  else if (location.pathname.startsWith("/production-dashboard")) selectedKey = "production-dashboard";
  else if (location.pathname.startsWith("/order-signed-delivery-note")) selectedKey = "order-signed-delivery-note";
  else if (location.pathname.startsWith("/production-orders")) selectedKey = "production-orders";
  else if (location.pathname.startsWith("/deliveries-operation")) selectedKey = "deliveries-operation";
  else if (location.pathname.startsWith("/home") || location.pathname === "/") selectedKey = "home";
  else if (location.pathname.startsWith("/products")) selectedKey = "products";
  else if (location.pathname.startsWith("/add-product")) selectedKey = "add-product";
  else if (location.pathname.startsWith("/add-new-bed")) selectedKey = "add-new-bed";
  else if (location.pathname.startsWith("/users")) selectedKey = "users";
  else if (location.pathname.startsWith("/requests")) selectedKey = "requests";
  else if (location.pathname.startsWith("/new-user-registration")) selectedKey = "new-user-registration";
  else if (location.pathname.startsWith("/add-new-employees")) selectedKey = "add-new-employees";
  else if (location.pathname.startsWith("/contact-us") || location.pathname.startsWith("/call-back-requests")) selectedKey = "contact-us";
  else if (location.pathname.startsWith("/variations")) selectedKey = "variations";
  else if (location.pathname.startsWith("/sizes")) selectedKey = "sizes";
  else if (location.pathname.startsWith("/categories")) selectedKey = "categories";
  else if (location.pathname.startsWith("/fabrics")) selectedKey = "fabrics";
  else if (location.pathname.startsWith("/storage-prices")) selectedKey = "storage-prices";
  else if (location.pathname.startsWith("/stockists-admin"))
  selectedKey = "stockists-admin";
  else if (location.pathname.startsWith("/news")) selectedKey = "news";
  else if (location.pathname.startsWith("/urgent-orders"))
  selectedKey = "urgent-orders";
  else if (location.pathname.startsWith("/downloads-admin"))
  selectedKey = "downloads-admin";

  // Which submenus to open by default
  const openKeys = [];
  if (
    location.pathname.startsWith("/all-orders") ||
    location.pathname.startsWith("/order-signed-delivery-note") ||
    location.pathname.startsWith("/deliveries-operation")
  ) {
    openKeys.push("orders");
  }
  // Add production submenu to open keys ONLY if user has a production role
  if (
    isProdRole &&
    (location.pathname.startsWith("/production-dashboard") ||
      location.pathname.startsWith("/production-orders"))
  ) {
    openKeys.push("production");
  }
  // Top-level products submenu
  if (
    (isSuperAdminOrAdmin && // <-- changed to include admin
      (location.pathname.startsWith("/users") ||
        location.pathname.startsWith("/requests") ||
        location.pathname.startsWith("/new-user-registration") ||
        location.pathname.startsWith("/add-new-employees"))) ||
    (location.pathname.startsWith("/products") ||
      location.pathname.startsWith("/add-product") ||
      location.pathname.startsWith("/add-new-bed") ||
      location.pathname.startsWith("/variations") ||
      location.pathname.startsWith("/sizes") ||
      location.pathname.startsWith("/categories") ||
      location.pathname.startsWith("/fabrics") ||
      location.pathname.startsWith("/storage-prices"))
  ) {
    openKeys.push("products-main");
    // Open the matching inner section too:
    if (
      location.pathname.startsWith("/products") ||
      location.pathname.startsWith("/add-product") ||
      location.pathname.startsWith("/add-new-bed")
    ) {
      openKeys.push("products-core");
    } else if (location.pathname.startsWith("/variations")) {
      // ⬅ moved variations under catalog settings
      openKeys.push("products-catalog");
    } else if (
      location.pathname.startsWith("/sizes") ||
      location.pathname.startsWith("/categories") ||
      location.pathname.startsWith("/fabrics") ||
      location.pathname.startsWith("/storage-prices") ||
      location.pathname.startsWith("/stockists-admin")
    ) {
      openKeys.push("products-catalog");
    }
  }

  const handleMenuClick = ({ key }) => {
    // Prevent sales users from navigating to production pages (double-safety)
    if (isSalesUser && (key === "production-dashboard" || key === "production-orders")) {
      message.error("Access denied: production pages are not available for your role.");
      return;
    }

    if (key === "logout") return setLogoutModalVisible(true);
    if (key === "login") return navigate("/login");
    navigate(`/${key}`);
    if (isMobile) setDrawerVisible(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    notification.success({ message: "Logged out successfully!", placement: "topRight", duration: 2.4 });
    setLogoutModalVisible(false);
    navigate("/login");
  };

  const userMenu = (
    <Menu
      onClick={({ key }) => {
        if (key === "profile") navigate("/profile");
        if (key === "logout") setLogoutModalVisible(true);
      }}
    >
      <Menu.Item key="profile" icon={<UserOutlined />}>Profile</Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />}>Logout</Menu.Item>
    </Menu>
  );

  // Helper: mobile-only tooltip wrapper
  const L = ({ title, children }) =>
    isMobile ? <Tooltip title={title} placement="right">{children}</Tooltip> : children;

  // ✅ Restricted menu for Production roles ONLY (kept bands/styling and icons consistent)
  const prodMenuOnly = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[selectedKey]}
      defaultOpenKeys={["production"]}
      onClick={handleMenuClick}
      className="menu-bands"
      inlineCollapsed={!isMobile && collapsed}
      inlineIndent={16}
    >
      <SubMenu
        key="production"
        icon={<BuildOutlined />}
        title={
          <L title="Manage Production"><span className="menu-label">Manage Production</span></L>
        }
        className="band band-production"
      >
        <Menu.Item key="production-dashboard" className="band-child">
          <L title="Production Dashboard"><span><AppstoreOutlined /> Production Dashboard</span></L>
        </Menu.Item>
        <Menu.Item key="production-orders" className="band-child">
          <L title="Production Orders"><span><BuildOutlined /> Production Orders</span></L>
        </Menu.Item>
      </SubMenu>
    </Menu>
  );

  // Menu with uniform, polished bands (tooltips on MOBILE only)
  const menuComponent = isProdRole ? (
    prodMenuOnly
  ) : (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[selectedKey]}
      defaultOpenKeys={openKeys}
      onClick={handleMenuClick}
      className="menu-bands"
      inlineCollapsed={!isMobile && collapsed}
      inlineIndent={16}
    >
      {isDeliveryUser ? (
        <Menu.Item
          key="order-signed-delivery-note"
          icon={<CheckCircleOutlined />}
          className="band band-orders"
        >
          <L title="Delivery Service">
            <span className="menu-label">Delivery Service</span>
          </L>
        </Menu.Item>
      ) : isSalesUser ? (
        // NOTE: Sales users see only the orders-related submenu — NO production submenu here
        <>
          <SubMenu
            key="orders"
            icon={<CheckCircleOutlined />}
            title={
              <L title="Manage Orders">
                <span className="menu-label">Manage Orders</span>
              </L>
            }
            className="band band-orders"
          >
            <Menu.Item key="all-orders" className="band-child">
              <L title="All Orders"><span><UnorderedListOutlined /> All Orders</span></L>
            </Menu.Item>
            <Menu.Item key="deliveries-operation" className="band-child">
              <L title="Deliveries Operation"><span><TruckOutlined /> Deliveries Operation</span></L>
            </Menu.Item>
            <Menu.Item key="order-signed-delivery-note" className="band-child">
              <L title="Delivery Service"><span><FileDoneOutlined /> Delivery Service</span></L>
            </Menu.Item>
          </SubMenu>
        </>
      ) : (
        <>
          <Menu.Item key="home" icon={<AppstoreOutlined />} className="band band-home">
            <L title="Home"><span className="menu-label">Home</span></L>
          </Menu.Item>

          {isSuperAdminOrAdmin && ( // <-- changed: admin gets same Users menu
            <SubMenu
              key="users"
              icon={<TeamOutlined />}
              title={
                <L title="Users"><span className="menu-label">Users</span></L>
              }
              className="band band-users"
            >
              <Menu.Item key="users" className="band-child">
                <L title="All Users"><span><UserSwitchOutlined /> All Users</span></L>
              </Menu.Item>
              <Menu.Item key="requests" className="band-child">
                <L title="Approve Users"><span><SafetyCertificateOutlined /> Approve Users</span></L>
              </Menu.Item>
              <Menu.Item key="new-user-registration" className="band-child">
                <L title="Add New Customer"><span><UserAddOutlined /> Add New Customer</span></L>
              </Menu.Item>
              {/* ⬇️ Renamed label only: Add New Employee -> Add New Role */}
              <Menu.Item key="add-new-employees" className="band-child">
                <L title="Add New Role"><span><SolutionOutlined /> Add New Role</span></L>
              </Menu.Item>
            </SubMenu>
          )}

          {/* ────────── MANAGE ORDERS ────────── */}
          <SubMenu
            key="orders"
            icon={<CheckCircleOutlined />}
            title={
              <L title="Manage Orders"><span className="menu-label">Manage Orders</span></L>
            }
            className="band band-orders"
          >
            <Menu.Item key="all-orders" className="band-child">
              <L title="All Orders"><span><UnorderedListOutlined /> All Orders</span></L>
            </Menu.Item>
            <Menu.Item key="deliveries-operation" className="band-child">
              <L title="Deliveries Operation"><span><TruckOutlined /> Deliveries Operation</span></L>
            </Menu.Item>
            <Menu.Item key="order-signed-delivery-note" className="band-child">
              <L title="Delivery Service"><span><FileDoneOutlined /> Delivery Service</span></L>
            </Menu.Item>
            <Menu.Item key="urgent-orders" className="band-child">
              <L title="Urgent Orders">
                <span style={{ color: "#ffeb3b", fontWeight: 600 }}>
                  🚨 Urgent Orders
                </span>
              </L>
            </Menu.Item>
          </SubMenu>

          {/* ────────── MANAGE PRODUCTION ────────── */}
          <SubMenu
            key="production"
            icon={<BuildOutlined />}
            title={
              <L title="Manage Production"><span className="menu-label">Manage Production</span></L>
            }
            className="band band-production"
          >
            <Menu.Item key="production-dashboard" className="band-child">
              <L title="Production Dashboard"><span><AppstoreOutlined /> Production Dashboard</span></L>
            </Menu.Item>
            <Menu.Item key="production-orders" className="band-child">
              <L title="Production Orders"><span><BuildOutlined /> Production Orders</span></L>
            </Menu.Item>
          </SubMenu>

          {/* ────────── MANAGE PRODUCTS (with nested colored groups) ────────── */}
          {/* Products group (top-level now) */}
          <SubMenu
            key="products-core"
            icon={<AppstoreOutlined />}
            title={<span className="menu-label">Products</span>}
            className="band inner-band-products"
          >
            <Menu.Item key="products" className="band-child">
              <L title="All Products"><span><AppstoreOutlined /> All Products</span></L>
            </Menu.Item>
            {/* ⬇️ Reordered earlier */}
            <Menu.Item key="add-new-bed" className="band-child">
              <L title="Add New Bed"><span><PlusOutlined /> Add New Bed</span></L>
            </Menu.Item>
            <Menu.Item key="add-product" className="band-child">
              <L title="Add New Product"><span><PlusOutlined /> Add New Product</span></L>
            </Menu.Item>
          </SubMenu>

          {/* Catalog Settings group */}
          <SubMenu
            key="products-catalog"
            icon={<TagsOutlined />}
            title={<span className="menu-label">Catalog Settings</span>}
            className="band inner-band-catalog"
          >
            {/* Variations first under Catalog Settings */}
            <Menu.Item key="variations" className="band-child">
              <L title="Product Variations"><span><FormOutlined /> Product Variations</span></L>
            </Menu.Item>

            {/* ⬇️ Renamed label only: Storage Prices -> Add New Employee */}
            <Menu.Item key="storage-prices" className="band-child">
              <L title="Manage Storage Prices"><span><DollarCircleOutlined /> Manage Storage Prices</span></L>
            </Menu.Item>
            <Menu.Item key="sizes" className="band-child">
              <L title="Manage Sizes"><span><ColumnWidthOutlined /> Manage Sizes</span></L>
            </Menu.Item>
            <Menu.Item key="categories" className="band-child">
              <L title="Manage Categories"><span><TagsOutlined /> Manage Categories</span></L>
            </Menu.Item>
            <Menu.Item key="fabrics" className="band-child">
              <L title="Manage Fabrics"><span><SkinOutlined /> Manage Fabrics</span></L>
            </Menu.Item>
             <Menu.Item key="stockists-admin" className="band-child">
      <L title="Manage Stockists">
        <span><AppstoreOutlined /> Manage Stockists</span>
      </L>
    </Menu.Item>
          </SubMenu>


          <Menu.Item key="contact-us" icon={<MdOutlineLocalPhone />} className="band band-callback">
            <L title="Call Back Requests"><span className="menu-label">Call Back Requests</span></L>
          </Menu.Item>

          {isSuperAdminOrAdmin && ( // <-- changed: admin sees News
            <Menu.Item key="news" icon={<NotificationOutlined />} className="band band-news">
              <L title="News"><span className="menu-label">News</span></L>
            </Menu.Item>
          )}

          {isSuperAdminOrAdmin && ( // <-- changed: admin sees Announcements
  <Menu.Item 
    key="announcements-admin"
    icon={<NotificationOutlined />}
    className="band band-announcements"
  >
    <L title="Announcements">
      <span className="menu-label">Announcements</span>
    </L>
  </Menu.Item>
  
)}

{isSuperAdminOrAdmin && ( // <-- changed: admin sees FAQs
  <Menu.Item 
    key="faqs"
    icon={<QuestionCircleOutlined />}
    className="band band-faq"
  >
    <L title="FAQs">
      <span className="menu-label">FAQs</span>
    </L>
  </Menu.Item>
)}

{isSuperAdminOrAdmin && ( // <-- changed: admin sees Downloads Manager
  <Menu.Item
    key="downloads-admin"
    icon={<FolderOpenOutlined />}
    className="band band-downloads"
  >
    <L title="Downloads Manager">
      <span className="menu-label">Downloads Manager</span>
    </L>
  </Menu.Item>
)}

{isSuperAdminOrAdmin && ( // <-- changed: admin sees Upcoming Products
 <Menu.Item
  key="upcoming-products-admin"
  icon={<AppstoreOutlined />}
  className="band band-upcoming"
>
    <L title="Upcoming Products">
      <span className="menu-label">Upcoming Products</span>
    </L>
  </Menu.Item>
)}

{isSuperAdminOrAdmin && (
  <Menu.Item
    key="complaints-admin"
    icon={<ExclamationCircleOutlined />}
    className="band band-complaints"
  >
    <L title="Complaint Management">
      <span className="menu-label">Complaint Management</span>
    </L>
  </Menu.Item>
)}
        </>
      )}
    </Menu>
  );

  // RoleChip kept (unused in header)
  const RoleChip = () => (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 12px",
        borderRadius: 999,
        background: "#f5f7fb",
        border: "1px solid #e6eaf1",
        color: "#1f2937",
        fontSize: 12,
        fontWeight: 600,
      }}
      title="Your role"
    >
      <UserOutlined style={{ color: "#64748b" }} />
      <span>{roleLabel}</span>
    </div>
  );

  return (
    <>
      {/* ─────────────────── Polished, consistent band styling ─────────────────── */}
      <style>{`
        .ant-layout-sider { background: #0b1320 !important; }
        /* ⬅️ ensure the sider always sits above any fixed/affixed content */
        .ant-layout-sider { position: relative; z-index: 2000; }

        .ant-layout-sider .ant-layout-sider-children {
          display: flex;
          flex-direction: column;
          height: 100%;
          overflow: hidden;
        }
        .menu-scroll {
          flex: 1 1 auto;
          overflow-y: auto;
          overflow-x: hidden;
          padding-bottom: 12px;
        }

        .menu-scroll::-webkit-scrollbar { width: 8px; }
        .menu-scroll::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.18);
          border-radius: 8px;
        }
        .menu-scroll::-webkit-scrollbar-track { background: transparent; }
        .menu-scroll {
          scrollbar-color: rgba(255,255,255,0.18) transparent;
          scrollbar-width: thin;
        }

        :root {
          --home:     #0096a8;
          --users:    #7057d6;
          --orders:   #15a36e;
          --production: #8e44ad; /* NEW: Purple color for production */
          --products: #e15d4a;
          --callback: #2a4863;
          --news:     #c7a114;
          --announcements: #d55b2a;
          --faq:      #2f80ed;
          --upcoming:  #06b6d4; 
           --complaints: #e74c3c;

          /* inner-product groups */
          --products-core:      #4aa4e1;
          --products-vari:      #b46cd6;
          --products-catalog:   #f0823e;

          --txt:      #ffffff;
          --txt-dim:  rgba(255,255,255,0.9);
          --hover:    rgba(255,255,255,0.08);
          --active:   rgba(255,255,255,0.14);
          --shadow:   0 4px 14px rgba(0,0,0,0.18);
          --radius:   12px;
          --ring:     0 0 0 2px rgba(255,255,255,0.22);
        }


        .menu-bands,
        .menu-bands .ant-menu { background: transparent !important; border-inline-end: 0 !important; }

        .menu-bands .ant-menu-item,
        .menu-bands .ant-menu-submenu-title {
          margin: 8px 12px !important;
          padding: 0 12px !important;
          height: 44px !important;
          line-height: 44px !important;
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
          border-radius: var(--radius) !important;
          color: var(--txt) !important;
          box-shadow: var(--shadow);
          border: 1px solid rgba(255,255,255,0.08);
          width: calc(100% - 24px) !important;
          transition: transform .15s ease, box-shadow .15s ease, filter .15s ease;
        }
          .menu-bands .band-complaints.ant-menu-item {
  background: linear-gradient(135deg, #e86a5a, var(--complaints));
}

        .menu-bands .ant-menu-submenu-title { padding-right: 40px !important; position: relative; }
        .menu-bands .ant-menu-submenu-arrow { right: 14px !important; }

        .menu-bands .ant-menu-title-content {
          flex: 1 1 auto;
          min-width: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis.
        }

        :root {
  --downloads: #0ea5e9;
}

.menu-bands .band-downloads.ant-menu-item {
  background: linear-gradient(135deg, #38bdf8, var(--downloads));
}
        .menu-bands .ant-menu-item:focus-visible,
        .menu-bands .ant-menu-submenu-title:focus-visible {
          outline: none;
          box-shadow: var(--ring), var(--shadow);
        }
        .menu-bands .band-announcements.ant-menu-item {
  background: linear-gradient(135deg, #e86d38, var(--announcements));
}
  /* NEW: FAQ BAND COLOR */
.menu-bands .band-faq.ant-menu-item {
  background: linear-gradient(135deg, #3da1ff, var(--faq));
}
        .menu-bands .ant-menu-item .anticon,
        .menu-bands .ant-menu-submenu-title .anticon,
        .menu-bands .ant-menu-submenu-arrow { color: var(--txt) !important; font-size: 16px; }

        /* top-level bands */
        .menu-bands .band-home.ant-menu-item                 { background: linear-gradient(135deg, #00a8ba, var(--home)); }
        .menu-bands .band-users > .ant-menu-submenu-title    { background: linear-gradient(135deg, #7c64df, var(--users)); }
        .menu-bands .band-orders > .ant-menu-submenu-title   { background: linear-gradient(135deg, #19b87a, var(--orders)); }
        .menu-bands .band-production > .ant-menu-submenu-title { background: linear-gradient(135deg, #9b59b6, var(--production)); } /* NEW: Production color */
        .menu-bands .band-products > .ant-menu-submenu-title { background: linear-gradient(135deg, #ef6d57, var(--products)); }
        .menu-bands .band-callback.ant-menu-item             { background: linear-gradient(135deg, #315676, var(--callback)); }
        .menu-bands .band-news.ant-menu-item                 { background: linear-gradient(135deg, #d4ad1a, var(--news)); }
        .menu-bands .band-upcoming.ant-menu-item {
  background: linear-gradient(135deg, #22d3ee, var(--upcoming));
  border: 1px solid rgba(255,255,255,0.22);
}

        /* inner product colored sections (headers) */
        .menu-bands .inner-band-products > .ant-menu-submenu-title {
          background: linear-gradient(135deg, #66b8ee, var(--products-core));
        }
        .menu-bands .inner-band-variations > .ant-menu-submenu-title {
          background: linear-gradient(135deg, #c07ae2, var(--products-vari));
        }
        .menu-bands .inner-band-catalog > .ant-menu-submenu-title {
          background: linear-gradient(135deg, #f59a57, var(--products-catalog));
        }

        .menu-bands .band-orders .ant-menu-item[key="urgent-orders"] {
  background: linear-gradient(135deg, #ff6b6b, #c0392b);
}

        /* =========================
           NEW: themed dropdown panels & items
           ========================= */

        /* Products (blue) */
        li.ant-menu-submenu.inner-band-products > ul.ant-menu {
          background: rgba(74, 164, 225, 0.16) !important;
          border-radius: 10px;
          padding: 6px;
        }
        /* ⬇️ Stronger emphasis for the three "Manage Product" items */
        li.ant-menu-submenu.inner-band-products > ul.ant-menu .ant-menu-item.band-child {
          background: rgba(74, 164, 225, 0.36);
          color: #eaf4ff !important;
          border-radius: 8px;
          margin: 6px 8px;
          border: 1px solid rgba(255,255,255,0.20);
          font-weight: 600;
        }
        li.ant-menu-submenu.inner-band-products > ul.ant-menu .ant-menu-item.band-child:hover {
          background: rgba(74, 164, 225, 0.48);
          color: #ffffff !important;
        }
        li.ant-menu-submenu.inner-band-products > ul.ant-menu .ant-menu-item.band-child.ant-menu-item-selected {
          background: rgba(74, 164, 225, 0.76) !important;
          color: #ffffff !important;
          box-shadow: inset 0 0 0 2px rgba(255,255,255,0.4), 0 4px 12px rgba(0,0,0,0.2) !important;
          transform: translateY(-1px);
        }

        /* Production (purple) */
        li.ant-menu-submenu.band-production > ul.ant-menu {
          background: rgba(142, 68, 173, 0.16) !important;
          border-radius: 10px;
          padding: 6px;
        }
        li.ant-menu-submenu.band-production > ul.ant-menu .ant-menu-item.band-child {
          background: rgba(142, 68, 173, 0.24);
          color: #fbf5ff !important;
          border-radius: 8px;
          margin: 6px 8px;
        }
        li.ant-menu-submenu.band-production > ul.ant-menu .ant-menu-item.band-child:hover {
          background: rgba(142, 68, 173, 0.34);
          color: #ffffff !important;
        }
        li.ant-menu-submenu.band-production > ul.ant-menu .ant-menu-item.band-child.ant-menu-item-selected {
          background: rgba(142, 68, 173, 0.62) !important;
          color: #ffffff !important;
          box-shadow: inset 0 0 0 2px rgba(255,255,255,0.4), 0 4px 12px rgba(0,0,0,0.2) !important;
          transform: translateY(-1px);
        }

        /* Catalog Settings (orange) */
        li.ant-menu-submenu.inner-band-catalog > ul.ant-menu {
          background: rgba(240, 130, 62, 0.16) !important;
          border-radius: 10px;
          padding: 6px;
        }
        li.ant-menu-submenu.inner-band-catalog > ul.ant-menu .ant-menu-item.band-child {
          background: rgba(240, 130, 62, 0.24);
          color: #ffffff !important;
          border-radius: 8px;
          margin: 6px 8px;
        }
        li.ant-menu-submenu.inner-band-catalog > ul.ant-menu .ant-menu-item.band-child:hover {
          background: rgba(240, 130, 62, 0.34);
          color: #ffffff !important;
        }
        li.ant-menu-submenu.inner-band-catalog > ul.ant-menu .ant-menu-item.band-child.ant-menu-item-selected {
          background: rgba(240, 130, 62, 0.72) !important;
          color: #ffffff !important;
          box-shadow: inset 0 0 0 2px rgba(255,255,255,0.4), 0 4px 12px rgba(0,0,0,0.2) !important;
          transform: translateY(-1px);
        }

        /* FIX: Override any Ant Design default colors that might be changing the text */
        li.ant-menu-submenu.inner-band-catalog > ul.ant-menu .ant-menu-item.band-child .ant-menu-title-content,
        li.ant-menu-submenu.inner-band-catalog > ul.ant-menu .ant-menu-item.band-child .ant-menu-title-content span,
        li.ant-menu-submenu.inner-band-catalog > ul.ant-menu .ant-menu-item.band-child .ant-menu-title-content a {
          color: #ffffff !important;
        }

        li.ant-menu-submenu.inner-band-catalog > ul.ant-menu .ant-menu-item.band-child:hover .ant-menu-title-content,
        li.ant-menu-submenu.inner-band-catalog > ul.ant-menu .ant-menu-item.band-child:hover .ant-menu-title-content span,
        li.ant-menu-submenu.inner-band-catalog > ul.ant-menu .ant-menu-item.band-child:hover .ant-menu-title-content a {
          color: #ffffff !important;
        }

        li.ant-menu-submenu.inner-band-catalog > ul.ant-menu .ant-menu-item.band-child.ant-menu-item-selected .ant-menu-title-content,
        li.ant-menu-submenu.inner-band-catalog > ul.ant-menu .ant-menu-item.band-child.ant-menu-item-selected .ant-menu-title-content span,
        li.ant-menu-submenu.inner-band-catalog > ul.ant-menu .ant-menu-item.band-child.ant-menu-item-selected .ant-menu-title-content a {
          color: #ffffff !important;
        }

        .menu-bands .ant-menu-item:hover,
        .menu-bands .ant-menu-submenu-title:hover {
          filter: brightness(1.08);
          transform: translateY(-1px);
        }
        .menu-bands .ant-menu-item:active,
        .menu-bands .ant-menu-submenu-title:active {
          transform: translateY(0);
        }

        /* FIXED: Selected state for all menu items including band-child */
        .menu-bands .ant-menu-item-selected,
        .menu-bands .ant-menu-submenu-selected > .ant-menu-submenu-title,
        .menu-bands .band-child.ant-menu-item-selected {
          box-shadow: inset 0 0 0 2px rgba(255,255,255,0.4), var(--shadow) !important;
          filter: brightness(1.15);
          position: relative;
        }
        
        .menu-bands .ant-menu-item-selected::before,
        .menu-bands .ant-menu-submenu-selected > .ant-menu-submenu-title::before,
        .menu-bands .band-child.ant-menu-item-selected::before {
          content: "";
          position: absolute;
          left: -2px;
          top: 8px;
          bottom: 8px;
          width: 4px;
          border-radius: 3px;
          background: rgba(255,255,255,0.95);
          box-shadow: 0 0 8px rgba(255,255,255,0.6);
        }

        .menu-bands .ant-menu-submenu-open > .ant-menu-submenu-title {
          filter: brightness(1.14);
          box-shadow: inset 0 0 0 2px rgba(255,255,255,0.28), var(--shadow);
        }

        .menu-bands .band-users .ant-menu      { background: rgba(112,87,214,0.25) !important; }
        .menu-bands .band-orders .ant-menu     { background: rgba(21,163,110,0.25) !important; }
        .menu-bands .band-production .ant-menu { background: rgba(142,68,173,0.25) !important; } /* NEW: Production background */
        .menu-bands .band-products .ant-menu   { background: rgba(225,93,74,0.25) !important; }

        .menu-bands .band .ant-menu-item.band-child {
          margin: 6px 14px !important;
          height: 38px !important;
          line-height: 38px !important;
          padding: 0 12px !important;
          border-radius: 10px !important;
          color: var(--txt-dim) !important;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          width: calc(100% - 28px) !important;
          transition: background .15s ease, transform .15s ease, box-shadow .15s ease;
        }
        
        .menu-bands .band .ant-menu-item.band-child:hover {
          background: rgba(255,255,255,0.08);
          transform: translateX(2px);
        }
        
        /* FIXED: Selected state for band-child items in submenus */
        .menu-bands .band .ant-menu-item.band-child.ant-menu-item-selected {
          background: rgba(255,255,255,0.18) !important;
          color: #ffffff !important;
          box-shadow: inset 0 0 0 2px rgba(255,255,255,0.3), 0 4px 12px rgba(0,0,0,0.15) !important;
          transform: translateX(2px) translateY(-1px);
        }

        .menu-bands .ant-menu-sub .ant-menu-item,
        .menu-bands .ant-menu-sub .ant-menu-submenu-title { padding-left: 12px !important; }

        .ant-layout-sider .profile-wrap {
          display:flex; flex-direction:column; align-items:center; justify-content:center; height:120px;
        }
        .ant-layout-sider:not(.ant-layout-sider-collapsed) .profile-wrap {
          border-bottom: 1px solid rgba(255,255,255,0.06);
          margin-bottom: 4px;
        }

        .ant-layout-sider-collapsed .menu-bands .ant-menu-item,
        .ant-layout-sider-collapsed .menu-bands .ant-menu-submenu-title {
          margin: 8px !important;
          padding: 0 !important;
          width: calc(100% - 16px) !important;
          justify-content: center;
        }
        .ant-layout-sider-collapsed .menu-bands .ant-menu-submenu-title .ant-menu-submenu-arrow { display: none !important; }
        .ant-layout-sider-collapsed .menu-bands .ant-menu-title-content { display: none !important; }
        .ant-layout-sider-collapsed .menu-bands .ant-menu-item .anticon,
        .ant-layout-sider-collapsed .menu-bands .ant-menu-submenu-title .anticon { margin: 0 !important; }

        .ant-layout-sider:not(.ant-layout-sider-collapsed) .menu-bands .ant-menu-item,
        .ant-layout-sider:not(.ant-layout-sider-collapsed) .menu-bands .ant-menu-submenu-title {
          backdrop-filter: saturate(1.2) blur(2px);
        }

        body, #root, .ant-layout { overflow-x: hidden; }

        .header-right { display: inline-flex; align-items: center; gap: 12px; }
        .header-divider { width: 1px; height: 22px; background: #e5e7eb; }
        .icon-btn { color: #334155; }
        .icon-btn:hover { color: #1f2937; }

        /* ⬇ give affixed bars a lower z-index than the sider */
        .ant-affix { z-index: 1500; }
      `}</style>

      <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
        {isMobile ? (
          <Drawer
            placement="left"
            closable
            onClose={() => setDrawerVisible(false)}
            open={drawerVisible}
            bodyStyle={{ padding: 0 }}
            width={280}
          >
            <div className="menu-scroll">{menuComponent}</div>
          </Drawer>
        ) : (
          <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            trigger={null}
            width={230}
            /* ⬇ reinforce stacking on the sider element itself */
            collapsedWidth={84}
            style={{ position: "relative", zIndex: 2000 }}
          >
            <div className="profile-wrap">
              <Avatar size={64} src="/profile.png" alt="Profile" />
              {!collapsed && (
                <div style={{ marginTop: 10, color: "#fff", textAlign: "center" }}>
                  <strong style={{ display: "block" }}>{displayName}</strong>
                  <div style={{ fontSize: 12, opacity: 0.9 }}>{roleLabel}</div>
                </div>
              )}
            </div>
            <div className="menu-scroll">{menuComponent}</div>
          </Sider>
        )}

        <Layout style={{ background: "#fff" }}>
          <Header
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: 56,
              background: "#fff",
              borderBottom: "1px solid #f0f0f0",
              padding: "0 16px",
              position: "sticky",
              top: 0,
              zIndex: 1800, // below sider, above content
            }}
          >
            {/* Left: Collapse / Drawer toggle */}
            <Space align="center" size={12}>
              {isMobile ? (
                <MenuUnfoldOutlined style={{ fontSize: 18, cursor: "pointer" }} onClick={() => setDrawerVisible(true)} />
              ) : collapsed ? (
                <MenuUnfoldOutlined style={{ fontSize: 18, cursor: "pointer" }} onClick={() => setCollapsed(false)} />
              ) : (
                <MenuFoldOutlined style={{ fontSize: 18, cursor: "pointer" }} onClick={() => setCollapsed(true)} />
              )}
            </Space>

            <div style={{ flex: 1 }} />

            {/* Right: Notification icon + profile dropdown */}
           <div className="header-right">
  <Tooltip title="Notifications">
    <NotificationBell />
  </Tooltip>

  <span className="header-divider" />

  {isLoggedIn ? (
    <Dropdown overlay={userMenu} trigger={["click"]}>
      <Space style={{ cursor: "pointer" }}>
        <Avatar size="small" src="/profile.png" alt="Profile" />
        <DownOutlined />
      </Space>
    </Dropdown>
  ) : (
    <Button type="text" icon={<LoginOutlined />} onClick={() => navigate("/login")}>
      Login
    </Button>
  )}
</div>
          </Header>

          <Content style={{ padding: "10px 20px", position: "relative", zIndex: 1 }}>
            {/* force unmount/remount on route changes */}
            <Outlet key={location.pathname} />
          </Content>
        </Layout>

 {/* Chat widget - visible only for admins/production roles */}
        {(isSuperAdminOrAdmin || isProdRole) && <ChatWidget visible={true} />}
        <Modal
          title="Confirm Logout"
          open={logoutModalVisible}
          onOk={handleLogout}
          onCancel={() => setLogoutModalVisible(false)}
          okText="Yes, Logout"
          cancelText="Cancel"
        >
          <p>Are you sure you want to log out?</p>
        </Modal>
      </Layout>
    </>
  );
};

export default Dashboard;